/**
 * React Query hook for fetching collection items directly from the
 * collection items endpoint, bypassing the media streaming pipeline.
 *
 * This solves the bug where client-side filtering of paginated library
 * data would miss collection items not in the current page.
 */

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { UserRole } from "@/services/urlService";
import { queryClient } from "@/core/queryClient";
import * as collectionsService from "@/services/collectionsService";

const DEFAULT_PAGE_SIZE = 50;

/**
 * Query key factory for collection items (separate from media cache)
 */
export const collectionItemKeys = {
  all: (clientId: string) => ["collection-items", clientId] as const,
  items: (clientId: string, collectionId: string, filters?: Record<string, unknown>) =>
    [...collectionItemKeys.all(clientId), collectionId, filters ?? {}] as const,
  counts: (clientId: string, collectionId: string) =>
    ["collection-media-type-counts", clientId, collectionId] as const,
};

interface UseCollectionItemsOptions {
  clientId: string | undefined;
  collectionId: string | undefined;
  mediaType?: string;
  pageSize?: number;
}

/**
 * Fetch collection items with server-side filtering and pagination.
 * Returns MediaItem instances ready for grid rendering.
 */
export function useCollectionItemsQuery(options: UseCollectionItemsOptions) {
  const { clientId, collectionId, mediaType, pageSize = DEFAULT_PAGE_SIZE } = options;

  const queryKey = clientId && collectionId
    ? collectionItemKeys.items(clientId, collectionId, { mediaType })
    : ["collection-items", "none"];

  const infiniteQuery = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      if (!clientId || !collectionId) {
        return { items: [] as MediaItem[], total: 0, hasMore: false, nextOffset: 0 };
      }

      const response = await collectionsService.getCollectionItems(
        clientId,
        collectionId,
        {
          offset: pageParam,
          limit: pageSize,
          mediaType,
        }
      );

      const items = response.items.map((item) =>
        MediaItem.fromBackend(item, UserRole.ADMIN, clientId)
      );

      return {
        items,
        total: response.total,
        hasMore: pageParam + pageSize < response.total,
        nextOffset: pageParam + pageSize,
      };
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextOffset : undefined,
    initialPageParam: 0,
    enabled: !!clientId && !!collectionId,
    staleTime: 2 * 60 * 1000,
  });

  const allItems = infiniteQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const total = infiniteQuery.data?.pages[0]?.total ?? 0;
  const hasMore =
    infiniteQuery.data?.pages[infiniteQuery.data.pages.length - 1]?.hasMore ?? false;

  return {
    items: allItems,
    total,
    hasMore,
    isLoading: infiniteQuery.isLoading,
    isLoadingMore: infiniteQuery.isFetchingNextPage,
    loadMore: infiniteQuery.fetchNextPage,
    refetch: infiniteQuery.refetch,
    error: infiniteQuery.error,
  };
}

/**
 * Fetch media type counts for a collection (for default tab selection).
 */
export function useCollectionMediaTypeCounts(
  clientId: string | undefined,
  collectionId: string | undefined
) {
  return useQuery({
    queryKey: clientId && collectionId
      ? collectionItemKeys.counts(clientId, collectionId)
      : ["collection-media-type-counts", "none"],
    queryFn: async () => {
      if (!clientId || !collectionId) return {};
      return collectionsService.getCollectionMediaTypeCounts(clientId, collectionId);
    },
    enabled: !!clientId && !!collectionId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Cache invalidation utilities for collection items
 */
export const collectionItemCacheUtils = {
  invalidateCollectionItems(clientId: string, collectionId: string) {
    queryClient.invalidateQueries({
      queryKey: collectionItemKeys.items(clientId, collectionId),
    });
    queryClient.invalidateQueries({
      queryKey: collectionItemKeys.counts(clientId, collectionId),
    });
  },

  invalidateAllCollectionItems(clientId: string) {
    queryClient.invalidateQueries({
      queryKey: collectionItemKeys.all(clientId),
    });
  },
};
